import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { ReviewFileStatus } from '@common/reviews/enums/review-file-status.enum';
import { ReviewFileUserStatus } from '@common/reviews/enums/review-file-user-status.enum';
import { ReviewFileUser } from '@common/reviews/models/review-file-user.model';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { PropertyObservable } from '@common/util/property-observable.decorator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '@portal-core/general/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '@portal-core/general/components/error-dialog/error-dialog.component';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { ReviewFileUsersService } from '@portal-core/reviews/review-file-users/services/review-file-users.service';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { BehaviorSubject, Observable, combineLatest, filter, from, map, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'mc-flare-file-reviewer',
  templateUrl: './flare-file-reviewer.component.html',
  styleUrls: ['./flare-file-reviewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlareFileReviewerComponent implements OnInit {
  @ViewChild(FlareTextEditorComponent, { static: true }) flareTextEditor: FlareTextEditorComponent;
  @Input() reviewPackageId: number;
  @Input() reviewer: LicenseUser;
  @Input() reviewFile: ReviewFile;
  @Input() reviewFileUser: ReviewFileUser;
  @Input() featureSetVersion: number;
  @Input() showConditions?: boolean;

  @InputObservable('reviewFile') reviewFile$: Observable<ReviewFile>;

  @PropertyObservable('reviewSyncState') reviewSyncState$: Observable<CollabSessionSyncState>;
  @PropertyObservable('submitting') submitting$: Observable<boolean>;

  @Output() reviewerStatusUpdate: EventEmitter<ReviewFileUserStatus> = new EventEmitter<ReviewFileUserStatus>();
  @Output() createReview: EventEmitter<void> = new EventEmitter<void>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  CollabFileType: typeof CollabFileType = CollabFileType;
  CollabSessionSyncState: typeof CollabSessionSyncState = CollabSessionSyncState;
  ReviewFileUserStatus: typeof ReviewFileUserStatus = ReviewFileUserStatus;
  ReviewFileStatus: typeof ReviewFileStatus = ReviewFileStatus;

  buildId$: Observable<string>;
  docExceedsMaxSize: boolean;
  docExceedsMongoDBMaxSize: boolean;
  projectLanguage$: Observable<string>;
  reviewSyncState: CollabSessionSyncState;
  submitting: boolean;
  isRequestInProgress: boolean = false;
  submitDisabled$: Observable<boolean>;
  collabSessionErrorEventSubject: BehaviorSubject<CollabSessionErrorEvent> = new BehaviorSubject<CollabSessionErrorEvent>(null);

  constructor(
    private reviewFileUsersService: ReviewFileUsersService,
    private projectsService: ProjectsService,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    // Create the observable for the language of the review file's project
    this.projectLanguage$ = this.reviewFile$.pipe(
      switchMap(reviewFile => {
        if (reviewFile) {
          return this.projectsService.getProjectLanguage$(reviewFile.ProjectId, reviewFile.CommitId);
        } else {
          return of(null);
        }
      }
      )
    );

    // Create the observable for disabling the submit button
    this.submitDisabled$ = combineLatest([
      this.collabSessionErrorEventSubject,
      this.reviewSyncState$,
      this.submitting$,
    ]).pipe(
      // Disable the submit button if the doc is in error, the review sync state has not started or is loading, or if a submission is in progress
      map(([error, reviewSyncState, submitting]) => !!error || !reviewSyncState || reviewSyncState === CollabSessionSyncState.Loading || submitting)
    );
  }

  reload() {
    this.flareTextEditor?.reload();
  }

  onErrorChanged(error: CollabSessionErrorEvent) {
    this.collabSessionErrorEventSubject.next(error);
  }

  onExceedsMaxSizeChanged(exceedsMaxSize: boolean) {
    this.docExceedsMaxSize = exceedsMaxSize;
  }

  onExceedsMongoDBMaxSizeChanged(exceedsMongoDBMaxSize: boolean) {
    this.docExceedsMongoDBMaxSize = exceedsMongoDBMaxSize;
  }

  onSyncStateChanged(syncState: CollabSessionSyncState) {
    this.reviewSyncState = syncState;
    this.cdr.detectChanges();
  }

  onCreateNewReview() {
    this.createReview.emit();
  }

  onSubmitClicked() {
    // Store the review file id because the save is asynchronous meaning there is a chance that the review file will have changed by the time it has saved.
    const reviewFileId = this.reviewFile.Id;
    let continueWithSave$: Observable<boolean>;

    // If the doc is too big to sync then prompt the user to see if they still want to submit
    if (this.docExceedsMaxSize || this.docExceedsMongoDBMaxSize) {
      continueWithSave$ = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
        width: '36rem',
        data: {
          action: 'Submit',
          title: 'Submit Review File',
          prompt: 'You have unsaved changes. Do you still want to Submit?'
        }
      }).afterClosed();
    } else {
      continueWithSave$ = of(true);
    }

    continueWithSave$.pipe(
      filter(continueWithSave => continueWithSave),
      tap(() => this.submitting = true),
      switchMap(() => from(this.flareTextEditor.save())),
      switchMap(() => this.reviewFileUsersService.updateMyReviewFileUserStatus$(reviewFileId, this.reviewFileUser.Id, ReviewFileUserStatus.Submitted))
    ).subscribe({
      next: () => {
        this.submitting = false;
        this.flareTextEditor.markAsPristine();
        this.reviewerStatusUpdate.emit(ReviewFileUserStatus.Submitted);
      },
      error: error => {
        this.submitting = false;
        this.showError(error, 'Unable to submit the review file. Please try again later. If the problem persists, please contact Technical Support.');
      }
    });
  }

  onFileChanged(event: EditorChangeEvent, reviewFileId: number) {
    // A change event can be fired from collaborative changes or meta data loading. The user has only edited it if the event is dirty.
    if (!event.dirty || this.isRequestInProgress) {
      return;
    }

    // An owner isn't necessarily a reviewer so check if the review file user exists
    if (this.reviewFileUser && this.reviewFileUser.Status !== ReviewFileUserStatus.InProgress) {
      if (this.reviewFileUser.Status === ReviewFileUserStatus.Submitted) {
        this.snackBar.open(`Review file status changed from Submitted to In Progress.`, 'OK', { duration: SnackBarAutoDismissDurationMS });
      }

      this.isRequestInProgress = true; // Set the flag to indicate a request is in progress

      return this.reviewFileUsersService.updateMyReviewFileUserStatus$(reviewFileId, this.reviewFileUser.Id, ReviewFileUserStatus.InProgress).subscribe(() => {
        this.reviewerStatusUpdate.emit(ReviewFileUserStatus.InProgress);
        this.isRequestInProgress = false;
      }, () => {
        this.isRequestInProgress = false;
      });
    }
  }

  onSubEditorChanged(status: ReviewFileUserStatus) {
    this.reviewerStatusUpdate.emit(status);
  }

  showError(error: any, errorMessage: string) {
    this.dialog.open(ErrorDialogComponent, {
      ...ErrorDialogComponent.DialogConfig,
      data: {
        title: 'Changes Not Saved',
        message: errorMessage,
        errors: this.errorService.getErrorMessages(error)
      }
    });
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.showConditions = showConditions;
    this.showConditionsChange.emit(showConditions);
  }
}
